import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import { useAdminControls } from "../../../state/AdminControlsContext";
import { useAdminService } from "../../../data/adminService";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { formatNumberDe } from "../../../utils/format";
import type {
  AdminFeatureUsageSeries,
  AdminUsageFeature,
  AdminUsageSubStatus,
} from "../../../graphql/types";
import { CHART_COLORS } from "../chartTheme";
import { ChartCard } from "./ChartCard";
import "../chartSetup";
import "./FeatureUsageSection.css";

/** Display order. DIRECTED_LIVE exists in the enum but nothing emits it yet. */
const FEATURE_ORDER: AdminUsageFeature[] = [
  "SONG_SCREEN",
  "SETLIST_SCREEN",
  "GIG_SCREEN",
  "HOME_SCREEN",
  "EDITOR",
  "LIVE_MODE",
  "DIRECTED_LIVE",
];

const SUB_STATUSES: AdminUsageSubStatus[] = ["TRIAL", "ACTIVE"];

function minutes(seconds: number): number {
  return Math.round(seconds / 60);
}

/**
 * Feature usage (FE-9, §6.6).
 *
 * Deliberately its own labelled block below the KPI row rather than another
 * tile in it: the figures above are complete, these are a **sample** — users
 * can object, and measurements taken without a connection are dropped. Mixing
 * the two makes the curves read as something they are not (Masterliste 7.9),
 * which is also why the opt-out rate sits in the header rather than in a
 * tooltip: it is the size of the blind spot.
 */
export const FeatureUsageSection: React.FC = () => {
  const { t } = useTranslation();
  const { rangeVars } = useAdminControls();
  const service = useAdminService();
  const [picked, setPicked] = useState<AdminUsageSubStatus | null>(null);

  const { data, loading, error, reload } = useAsyncData(
    () => service.getFeatureUsage(rangeVars),
    [service, rangeVars.range, rangeVars.from, rangeVars.to],
  );

  const withData = useMemo(
    () => new Set((data?.series ?? []).map((series) => series.subStatus)),
    [data],
  );

  // Nothing is preselected until the data arrives: during the beta everyone is on
  // trial, and defaulting to a fixed status would open on an empty view whenever
  // the other one holds all the numbers.
  const subStatus: AdminUsageSubStatus =
    picked ?? (withData.has("ACTIVE") ? "ACTIVE" : withData.has("TRIAL") ? "TRIAL" : "ACTIVE");

  const withheld = data?.aggregated;
  const hasWithheld = Boolean(withheld && withheld.sessions > 0);

  const visible = useMemo(
    () =>
      FEATURE_ORDER.map((feature) =>
        (data?.series ?? []).find(
          (series) => series.feature === feature && series.subStatus === subStatus,
        ),
      ).filter((series): series is AdminFeatureUsageSeries => Boolean(series)),
    [data, subStatus],
  );

  if (error) {
    return (
      <section className="usage-section">
        <div className="lyra-error usage-error">
          <span>{t("dashboard.featureUsage.error")}</span>
          <button className="range-btn range-btn--ghost" onClick={reload}>
            {t("dashboard.retry")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="usage-section">
      <header className="usage-head">
        <div>
          <h2 className="usage-title">{t("dashboard.featureUsage.title")}</h2>
          <p className="usage-caption">{t("dashboard.featureUsage.caption")}</p>
        </div>

        <div className="usage-head-right">
          <div className="usage-optout" title={t("dashboard.featureUsage.optOutHint")}>
            <span className="usage-optout-label">
              {t("dashboard.featureUsage.optOutLabel")}
            </span>
            <span className="usage-optout-value">
              {data
                ? t("dashboard.featureUsage.optOutValue", {
                    pct: formatNumberDe(Math.round(data.optOut.rate * 1000) / 10),
                    count: formatNumberDe(data.optOut.optedOut),
                    total: formatNumberDe(data.optOut.users),
                  })
                : "—"}
            </span>
          </div>

          <div className="usage-toggle" role="group">
            {SUB_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                className={`usage-seg${subStatus === status ? " usage-seg--active" : ""}`}
                onClick={() => setPicked(status)}
              >
                {t(`dashboard.featureUsage.subStatus.${status}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {!data && loading ? (
        <div className="usage-grid">
          {[0, 1, 2].map((i) => (
            <div className="lyra-skeleton usage-skeleton" key={i} />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className="usage-empty">{emptyReason(t, subStatus, withData, hasWithheld)}</p>
      ) : (
        <div className="usage-grid">
          {visible.map((series) => (
            <FeatureChart
              key={`${series.feature}-${series.subStatus}`}
              series={series}
              title={t(`dashboard.featureUsage.feature.${series.feature}`)}
            />
          ))}
        </div>
      )}

      <div className="usage-notes">
        {hasWithheld && (
          <p className="usage-note">
            {t("dashboard.featureUsage.withheld", {
              minutes: formatNumberDe(minutes(withheld!.seconds)),
              sessions: formatNumberDe(withheld!.sessions),
              threshold: data?.minimumCellSessions ?? 5,
            })}
          </p>
        )}
        {data?.bucketsCollapsedToDays && (
          <p className="usage-note">{t("dashboard.featureUsage.dailyBuckets")}</p>
        )}
        <p className="usage-note">{t("dashboard.featureUsage.liveModeNote")}</p>
      </div>
    </section>
  );
};

/**
 * An empty chart area has three quite different causes, and saying which one it
 * is saves the reader from assuming the measurement is broken.
 */
function emptyReason(
  t: (key: string) => string,
  subStatus: AdminUsageSubStatus,
  withData: Set<AdminUsageSubStatus>,
  hasWithheld: boolean,
): string {
  const other: AdminUsageSubStatus = subStatus === "TRIAL" ? "ACTIVE" : "TRIAL";
  if (withData.has(other)) {
    return t(`dashboard.featureUsage.emptyOther.${other}`);
  }
  if (hasWithheld) {
    return t("dashboard.featureUsage.emptyWithheld");
  }
  return t("dashboard.featureUsage.empty");
}

interface FeatureChartProps {
  series: AdminFeatureUsageSeries;
  title: string;
}

const FeatureChart: React.FC<FeatureChartProps> = ({ series, title }) => {
  const { t } = useTranslation();

  const data = useMemo<ChartData<"line">>(
    () => ({
      labels: series.points.map((point) => point.label),
      datasets: [
        {
          data: series.points.map((point) => minutes(point.seconds)),
          borderColor: CHART_COLORS.accent,
          backgroundColor: CHART_COLORS.accentArea,
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          // Points the server withheld are drawn hollow, so a zero that means
          // "too few sessions to show" is not read as "nobody used it".
          pointRadius: series.points.map((point) => (point.aggregated ? 4 : 2.5)),
          pointStyle: series.points.map((point) => (point.aggregated ? "crossRot" : "circle")),
          pointHoverRadius: 5,
          pointBackgroundColor: CHART_COLORS.accent,
          pointBorderColor: CHART_COLORS.accent,
        },
      ],
    }),
    [series],
  );

  const options = useMemo<ChartOptions<"line">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: CHART_COLORS.tooltipBg,
          borderColor: CHART_COLORS.tooltipBorder,
          borderWidth: 1,
          titleColor: CHART_COLORS.tick,
          bodyColor: CHART_COLORS.tooltipText,
          bodyFont: { weight: "bold", size: 14 },
          padding: 10,
          cornerRadius: 10,
          displayColors: false,
          callbacks: {
            label: (ctx) => {
              const point = series.points[ctx.dataIndex];
              if (point?.aggregated) {
                return t("dashboard.featureUsage.tooltipWithheld");
              }
              return t("dashboard.featureUsage.tooltip", {
                minutes: formatNumberDe(Number(ctx.parsed.y ?? 0)),
                sessions: formatNumberDe(point?.sessions ?? 0),
              });
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: CHART_COLORS.tick, font: { size: 11 }, maxTicksLimit: 6, autoSkip: true },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_COLORS.grid },
          border: { display: false },
          ticks: {
            color: CHART_COLORS.tick,
            font: { size: 11 },
            maxTicksLimit: 5,
            callback: (v) => formatNumberDe(Number(v)),
          },
        },
      },
    }),
    [series, t],
  );

  return (
    <ChartCard
      title={title}
      headerRight={
        <span className="usage-total">
          {t("dashboard.featureUsage.total", {
            minutes: formatNumberDe(minutes(series.totalSeconds)),
            sessions: formatNumberDe(series.totalSessions),
          })}
        </span>
      }
    >
      <Line data={data} options={options} />
    </ChartCard>
  );
};
